import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import AlertCard from '../components/AlertCard';
import { SkeletonList } from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';
import QuickStats from '../components/QuickStats';
import { db, saveAlerts, Alert, queueSync } from '../lib/db';
import { useNetwork } from '../contexts/NetworkContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { syncManager } from '../lib/syncManager';
import toast from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export default function Alerts() {
  const { isOnline } = useNetwork();
  const { socket, isConnected } = useWebSocket();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch alerts from API (disabled in demo mode)
  const { data: apiAlerts, refetch, isLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/alerts`);
      return response.data as Alert[];
    },
    enabled: false, // Disabled - using offline data only for demo
    staleTime: 30000,
  });

  // Save to offline storage when fetched
  useEffect(() => {
    if (apiAlerts) {
      saveAlerts(apiAlerts);
    }
  }, [apiAlerts]);

  // Load from offline storage
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    const loadOfflineData = async () => {
      const offlineAlerts = await db.alerts.orderBy('timestamp').reverse().toArray();
      if (offlineAlerts.length > 0) {
        setAlerts(offlineAlerts);
      }
    };
    loadOfflineData();
  }, []);

  // Update display when online data arrives
  useEffect(() => {
    if (apiAlerts) {
      setAlerts(apiAlerts);
    }
  }, [apiAlerts]);

  // WebSocket real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewAlert = async (newAlert: Alert) => {
      // Save to IndexedDB
      await db.alerts.put(newAlert);
      
      // Add to UI (at top of list)
      setAlerts(prev => [newAlert, ...prev]);
      
      // Show notification
      toast.error(`New ${newAlert.severity || 'alert'}: ${newAlert.title}`, {
        duration: 5000,
        icon: '🚨',
      });
    };

    const handleAlertAcknowledged = async (data: { alertId: string }) => {
      // Update IndexedDB
      await db.alerts.update(data.alertId, { acknowledged: true });
      
      // Update UI
      setAlerts(prev =>
        prev.map(alert =>
          alert.id === data.alertId ? { ...alert, acknowledged: true } : alert
        )
      );
    };

    socket.on('alert:new', handleNewAlert);
    socket.on('alert:acknowledged', handleAlertAcknowledged);

    return () => {
      socket.off('alert:new', handleNewAlert);
      socket.off('alert:acknowledged', handleAlertAcknowledged);
    };
  }, [socket, isConnected]);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline) {
      syncManager.syncPendingChanges().then(({ success, failed }) => {
        if (success > 0) {
          toast.success(`Synced ${success} changes`);
          refetch();
        }
        if (failed > 0) {
          toast.error(`Failed to sync ${failed} changes`);
        }
      });
    }
  }, [isOnline, refetch]);

  // Handle alert acknowledgment (with optimistic update)
  const handleAcknowledge = async (alertId: string) => {
    // Optimistic update - update UI immediately
    setAlerts(prevAlerts =>
      prevAlerts.map(alert =>
        alert.id === alertId ? { ...alert, acknowledged: true, syncStatus: 'pending' as const } : alert
      )
    );

    // Update IndexedDB
    await db.alerts.update(alertId, { acknowledged: true, syncStatus: 'pending' });

    if (isOnline) {
      // Try to sync immediately if online
      try {
        await axios.post(`${API_BASE_URL}/alerts/${alertId}/acknowledge`);
        await db.alerts.update(alertId, { syncStatus: 'synced' });
        toast.success('Alert acknowledged');
      } catch (error) {
        // Queue for later if sync fails
        await queueSync('update', 'alert', alertId, { acknowledged: true });
        await db.alerts.update(alertId, { syncStatus: 'error' });
        toast.error('Will sync when connection improves');
      }
    } else {
      // Queue for sync when offline
      await queueSync('update', 'alert', alertId, { acknowledged: true });
      toast.success('Alert acknowledged (will sync when online)');
    }
  };

  // Pull to refresh
  const handleRefresh = async () => {
    if (!isOnline) {
      toast.error('Cannot refresh while offline');
      return;
    }

    setIsRefreshing(true);
    try {
      await refetch();
      toast.success('Alerts refreshed');
    } catch (error) {
      toast.error('Failed to refresh alerts');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Calculate stats
  const critical = alerts.filter(a => a.severity === 'critical').length;
  const high = alerts.filter(a => a.severity === 'high').length;
  const unacknowledged = alerts.filter(a => !a.acknowledged).length;
  const acknowledged = alerts.filter(a => a.acknowledged).length;

  const stats = [
    {
      title: 'Total Alerts',
      value: alerts.length,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      color: 'peacock' as const,
    },
    {
      title: 'Critical',
      value: critical,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'critical' as const,
    },
    {
      title: 'High Priority',
      value: high,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      color: 'amber' as const,
    },
    {
      title: 'Acknowledged',
      value: `${acknowledged}/${alerts.length}`,
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'mint' as const,
    },
  ];

  return (
    <div className="page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clinical Alerts</h1>
          <p className="text-gray-600 mt-1">Real-time notifications for critical events</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={!isOnline || isRefreshing}
          className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRefreshing ? (
            <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Refresh
        </button>
      </div>

      {/* Quick Stats */}
      {alerts.length > 0 && <QuickStats stats={stats} />}

      {!isOnline && alerts.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-800">
            You're offline and no cached alerts are available. Data will load when connection is restored.
          </p>
        </div>
      )}

      {isLoading && alerts.length === 0 ? (
        <SkeletonList count={5} type="alert" />
      ) : alerts.length === 0 ? (
        <EmptyState
          icon="alerts"
          title="All clear!"
          description="No active alerts at the moment. You'll be notified here when critical events occur."
          action={isOnline ? {
            label: 'Refresh Alerts',
            onClick: handleRefresh
          } : undefined}
        />
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
          ))}
        </div>
      )}
    </div>
  );
}

